import { Leaf, ChevronDown, Info, Flag, Pencil } from 'lucide-react';

interface SideInfoPanelProps {
  formData: any;
  setFormData: (data: any) => void;
  projects: any[];
  zones: any[];
  proposalGroups: any[];
  proposalCategories: any[];
  proposalPurposes: any[];
  profile: any;
}

export function SideInfoPanel({ 
  formData, 
  setFormData, 
  projects, 
  zones, 
  proposalGroups, 
  proposalCategories, 
  proposalPurposes, 
  profile 
}: SideInfoPanelProps) {
  return (
    <div className="flex flex-col gap-10 lg:pl-6">
      <section className="space-y-6">
        <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide uppercase">Thông tin liên quan</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-[140px_1fr] items-start gap-2">
            <span className="sm:text-sm text-xs text-slate-500">Khu vực</span>
            <span className="sm:text-sm text-xs text-slate-900">
              {(() => {
                const z = zones.find((z: any) => z.id === formData.zoneId);
                return z ? `${z.code ? `[${z.code}] ` : ''}${z.name}` : 'Chưa chọn';
              })()}
            </span>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-start gap-2">
            <span className="sm:text-sm text-xs text-slate-500">Dự án / Công việc</span>
            <span className="sm:text-sm text-xs text-slate-900">
              {(() => {
                const project = projects.find((p: any) => p.id === formData.projectId);
                return project ? `[${project.code}] ${project.name}` : 'Chưa chọn';
              })()}
            </span>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-start gap-2">
            <span className="sm:text-sm text-xs text-slate-500">Loại đề xuất</span>
            <div>
              <span className="inline-flex items-center rounded-sm bg-emerald-50 px-2 py-0.5 sm:text-sm text-xs text-emerald-600">
                {formData.requestType === 'thi_cong' ? 'Thi công' : 'Bảo dưỡng'}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-start gap-2">
            <span className="sm:text-sm text-xs text-slate-500">Người đề xuất</span>
            <span className="sm:text-sm text-xs text-slate-900">{profile?.full_name || 'Đang tải...'}</span>
          </div>
          <div className="grid grid-cols-[140px_1fr] items-start gap-2">
            <span className="sm:text-sm text-xs text-slate-500">Ngày đề xuất</span>
            <span className="sm:text-sm text-xs text-slate-900">{new Date().toLocaleDateString('vi-VN')}</span>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide uppercase">Phân loại đề xuất</h3>
        <div className="space-y-5">
          <div className="space-y-2">
            <label className="sm:text-sm text-xs text-slate-700">Nhóm đề xuất <span className="text-rose-500">*</span></label>
            <div className="relative">
              <select 
                value={formData.group}
                onChange={e => setFormData((prev: any) => ({ ...prev, group: e.target.value }))}
                className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pl-10 pr-10 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
              >
                <option value="">Chọn nhóm...</option>
                {proposalGroups.map((g: any) => (
                  <option key={g.id} value={g.code}>{g.name}</option>
                ))}
              </select>
              <Leaf className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="sm:text-sm text-xs text-slate-700">Danh mục <span className="text-rose-500">*</span></label>
            <div className="relative">
              <select 
                value={formData.category}
                onChange={e => setFormData((prev: any) => ({ ...prev, category: e.target.value }))}
                className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pr-10 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all disabled:opacity-50"
                disabled={!formData.group}
              >
                <option value="">Chọn danh mục...</option>
                {proposalCategories.map((c: any) => (
                  <option key={c.id} value={c.code}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="sm:text-sm text-xs text-slate-700">Mục đích <span className="text-rose-500">*</span></label>
            <div className="relative">
              <select 
                value={formData.purpose}
                onChange={e => setFormData((prev: any) => ({ ...prev, purpose: e.target.value }))}
                className="h-11 w-full appearance-none rounded-md border border-slate-200 bg-white px-3 pr-10 sm:text-sm text-xs text-slate-900 focus:border-emerald-500 focus:outline-none transition-all"
              >
                <option value="">Chọn mục đích...</option>
                {proposalPurposes.map((p: any) => (
                  <option key={p.id} value={p.code}>{p.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide uppercase">Độ ưu tiên gợi ý</h3>
          <Info className="h-4 w-4 text-slate-400 cursor-help" />
        </div>
        
        <p className="sm:text-sm text-xs text-slate-500">Dựa trên các thông tin bạn đã nhập, hệ thống gợi ý mức độ ưu tiên:</p>
        
        <div className="rounded-md border border-amber-100 bg-amber-50/50 p-4">
          <div className="flex items-center gap-2 mb-2 text-amber-500">
            <Flag className="h-4 w-4" />
            <span className="sm:text-sm text-xs">Trung bình</span>
          </div>
          <p className="sm:text-sm text-xs text-slate-500">Đề xuất không ảnh hưởng nghiêm trọng, có thể thực hiện trong kế hoạch.</p>
        </div>
        
        <button type="button" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition-colors sm:text-sm text-xs mt-2">
          <Pencil className="h-3 w-3" />
          <span>Thay đổi ưu tiên</span>
        </button>
      </section>
    </div>
  );
}
